import { cn } from "@-/com.npmjs.cn";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import {
  type SentenceDrillState,
  sentenceDrillTokenDisplay,
} from "@-/dev.improve.haniverse.lib.sentence.drill";
import { View } from "@-/com.npmjs.react-native";

/**
 * A blank stands in for one character the user still owes.
 *
 * Sized to the word rather than shown as a single placeholder so the sentence keeps its shape: the
 * learner can see how many characters are missing (a real signal about what they're being asked
 * for), and the layout does not jump when a word fills in.
 */
function Blanks({ count }: { count: number }) {
  return (
    <Text className="text-[22px] leading-tight text-muted-foreground">{"＿".repeat(count)}</Text>
  );
}

/**
 * The sentence under construction: one column per jieba token, pinyin above, glyphs below.
 *
 * Modelled on the browse `sentence-row`, which already solves the ruby-style layout — but this one
 * is status-driven rather than tap-to-navigate, so it is a clone rather than a reuse. Everything it
 * shows comes from `sentenceDrillTokenDisplay`, which owns the mode-dependent rules (in speech mode
 * a hint uncovers glyphs and never the reading; in character mode the reverse).
 */
export function DrillPageSentenceStrip({ state }: { state: SentenceDrillState }) {
  return (
    <View
      accessibilityLabel="Sentence answer"
      className="w-full flex-row flex-wrap items-end justify-center gap-x-1 gap-y-2 px-2"
      testID="sentence-drill-strip"
    >
      {state.tokens.map((token, index) => {
        const display = sentenceDrillTokenDisplay(state, index);
        const isCursor = index === state.cursorIndex;
        return (
          <View
            className={cn(
              "items-center rounded-xs px-1",
              // The cursor marks where the drill is waiting, so "which blank am I on" is never
              // ambiguous when several are outstanding.
              isCursor && "bg-muted",
            )}
            key={`${String(index)}-${token.word}`}
            testID={`sentence-drill-token-${String(index)}`}
          >
            <Text className="h-4 text-[10px] leading-tight text-muted-foreground">
              {display.pinyinText === null ? "" : toDiacriticPinyin(display.pinyinText)}
            </Text>
            <View className="flex-row items-end">
              {display.glyphText === null ? null : (
                <Text
                  className={cn(
                    "text-[22px] leading-tight text-foreground",
                    // Bold is the "you were given this one — study it" signal. It is the only
                    // weight change in the strip, so it reads unambiguously.
                    display.isBold && "font-bold text-primary",
                  )}
                >
                  {display.glyphText}
                </Text>
              )}
              {display.pendingCharacterCount > 0 && (
                <Blanks count={display.pendingCharacterCount} />
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
}
